import { useEffect, useMemo, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { http } from "../lib/api";
import { Seo } from "../components/Seo";
import { SiteHeader } from "../components/SiteHeader";
import { SiteFooter } from "../components/SiteFooter";
import {
  Search,
  ArrowUpRight,
  Building2,
  Compass,
  Home as HomeIcon,
  ShieldCheck,
} from "lucide-react";

/** Extracts a best-guess institution query from an arbitrary 404 path.
 *  e.g. "/nbfc/nbfc-tata-capital-housing-finance-xyz"
 *        → "tata capital housing finance xyz"
 */
const deriveQuery = (path) => {
  if (!path) return "";
  const segs = path.split("/").filter(Boolean);
  const last = segs[segs.length - 1] || "";
  return last.replace(/^(nbfc|bank)-/, "").replace(/-+/g, " ").trim();
};

export default function NotFound({ sourcePath }) {
  const location = useLocation();
  const navigate = useNavigate();
  const path = sourcePath || location.pathname;

  const initialQuery = useMemo(() => deriveQuery(path), [path]);
  const [q, setQ] = useState(initialQuery);
  const [suggestions, setSuggestions] = useState(null);
  const [loading, setLoading] = useState(false);

  const fetchSuggest = async (term) => {
    if (!term || term.trim().length < 2) {
      setSuggestions([]);
      return;
    }
    setLoading(true);
    try {
      const { data } = await http.get("/search/suggest", {
        params: { q: term, limit: 8 },
      });
      setSuggestions(data.suggestions || []);
    } catch {
      setSuggestions([]);
    } finally {
      setLoading(false);
    }
  };

  // Debounced fetch whenever `q` changes
  useEffect(() => {
    const t = setTimeout(() => fetchSuggest(q), 250);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q]);

  const onSubmit = (e) => {
    e.preventDefault();
    const term = q.trim();
    if (!term) return;
    navigate(`/?q=${encodeURIComponent(term)}#directory`);
  };

  const topPick = suggestions && suggestions.length > 0 ? suggestions[0] : null;

  return (
    <div className="min-h-screen bg-white text-[#0A0A0A] flex flex-col">
      {/*
        noindex, follow — we don't want search engines to index the 404 itself,
        but we DO want them to crawl the internal links on this page so link
        equity still flows back to the main directory.
      */}
      <Seo
        title="Page not found"
        description="The institution or page you're looking for isn't in our directory. Try a search — we index 9,381 RBI-registered Banks & NBFCs across India."
        path={path}
        noindex
        robots="noindex, follow, max-snippet:-1"
      />
      <SiteHeader />

      <main className="flex-1" data-testid="notfound-page">
        {/* Hero */}
        <section className="border-b border-[#0A0A0A]">
          <div className="max-w-[1200px] mx-auto px-6 md:px-8 lg:px-12 py-16 md:py-20">
            <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#FF3333] mb-3">
              404 · route not found
            </div>
            <h1
              className="font-display font-black text-5xl md:text-7xl tracking-tighter leading-[0.98] max-w-3xl"
              data-testid="notfound-title"
            >
              We couldn't find that page.
            </h1>
            <p className="mt-5 max-w-xl text-base md:text-lg text-[#4B5563] leading-relaxed">
              {initialQuery ? (
                <>
                  You asked for{" "}
                  <span className="font-mono text-[13px] bg-[#0A0A0A] text-white px-1.5 py-0.5 break-all">
                    {path}
                  </span>
                  . It's not in our directory — yet. But one of these 9,381
                  RBI-registered institutions might be what you meant:
                </>
              ) : (
                <>
                  The link you followed is out of date or mistyped. Search our
                  public directory of 9,381 RBI-registered Banks & NBFCs below.
                </>
              )}
            </p>

            {/* Search */}
            <form
              onSubmit={onSubmit}
              className="mt-8 flex flex-col sm:flex-row gap-2 max-w-2xl"
              data-testid="notfound-search-form"
            >
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#4B5563]" />
                <input
                  type="text"
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                  placeholder="Try 'HDFC', 'Bajaj Finance', 'SBI Cards'…"
                  autoFocus
                  data-testid="notfound-search-input"
                  className="w-full border border-[#0A0A0A] bg-white pl-10 pr-4 py-3 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-[#002FA7]"
                />
              </div>
              <button
                type="submit"
                data-testid="notfound-search-submit"
                className="bg-[#0A0A0A] text-white font-bold px-5 py-3 text-sm tracking-wide hover:bg-[#002FA7] transition-colors"
              >
                Search directory
              </button>
            </form>
          </div>
        </section>

        {/* Did you mean + Suggestions */}
        <section className="border-b border-[#0A0A0A] bg-[#F8F9FA]">
          <div className="max-w-[1200px] mx-auto px-6 md:px-8 lg:px-12 py-12">
            <div className="flex items-baseline justify-between border-b border-[#0A0A0A] pb-2 mb-6">
              <div>
                <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#4B5563]">
                  Fuzzy match
                </div>
                <h2 className="font-display font-black text-2xl md:text-3xl tracking-tight mt-0.5">
                  {topPick ? "Did you mean…" : "Suggestions"}
                </h2>
              </div>
              {loading && (
                <div
                  data-testid="notfound-loading"
                  className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#4B5563]"
                >
                  Matching…
                </div>
              )}
            </div>

            {suggestions === null ? (
              <div className="text-sm text-[#4B5563]">Loading…</div>
            ) : suggestions.length === 0 ? (
              <div
                className="p-8 border border-dashed border-[#D1D5DB] bg-white text-center"
                data-testid="notfound-no-suggestions"
              >
                <Compass className="w-6 h-6 mx-auto mb-3 text-[#4B5563]" />
                <p className="text-sm text-[#4B5563]">
                  No close matches in our database.
                  <br />
                  Try a different spelling, or{" "}
                  <Link to="/" className="text-[#002FA7] underline">
                    browse the full directory
                  </Link>
                  .
                </p>
              </div>
            ) : (
              <>
                {/* Top pick — big card */}
                {topPick && (
                  <Link
                    to={topPick.url}
                    data-testid={`notfound-top-${topPick.slug}`}
                    className="block border-2 border-[#0A0A0A] p-6 bg-white hover:bg-[#0A0A0A] hover:text-white transition-colors group mb-5"
                  >
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.2em] text-[#4B5563] group-hover:text-white/60 mb-2">
                          <span>Top pick</span>
                          {topPick.verified && (
                            <span className="inline-flex items-center gap-1 bg-emerald-600 text-white px-1.5 py-0.5 text-[9px]">
                              <ShieldCheck className="w-2.5 h-2.5" />
                              VERIFIED
                            </span>
                          )}
                        </div>
                        <div className="font-display font-black text-2xl md:text-3xl tracking-tight leading-tight break-words">
                          {topPick.name}
                        </div>
                        <div className="mt-2 text-sm text-[#4B5563] group-hover:text-white/70">
                          {topPick.category} · {topPick.hq_city}, {topPick.hq_state}
                        </div>
                      </div>
                      <ArrowUpRight className="w-7 h-7 flex-shrink-0" />
                    </div>
                  </Link>
                )}

                {/* Remaining — grid */}
                {suggestions.length > 1 && (
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                    {suggestions.slice(1).map((s) => (
                      <Link
                        key={s.slug}
                        to={s.url}
                        data-testid={`notfound-suggest-${s.slug}`}
                        className="block border border-[#E5E7EB] hover:border-[#0A0A0A] bg-white p-4 transition-colors group"
                      >
                        <div className="flex items-start justify-between gap-2">
                          <div className="font-mono text-[9px] uppercase tracking-[0.2em] text-[#4B5563]">
                            {s.category}
                          </div>
                          {s.verified && (
                            <ShieldCheck className="w-3 h-3 text-emerald-600" title="Verified contacts" />
                          )}
                        </div>
                        <div className="mt-1 font-display font-bold text-sm leading-tight group-hover:text-[#002FA7] line-clamp-2">
                          {s.name}
                        </div>
                        <div className="mt-1 font-mono text-[10px] text-[#4B5563]">
                          {s.hq_city}, {s.hq_state}
                        </div>
                      </Link>
                    ))}
                  </div>
                )}
              </>
            )}
          </div>
        </section>

        {/* Escape hatches — internal link hub for SEO crawl depth */}
        <section className="border-b border-[#0A0A0A]">
          <div className="max-w-[1200px] mx-auto px-6 md:px-8 lg:px-12 py-12">
            <div className="flex items-baseline justify-between border-b border-[#0A0A0A] pb-2 mb-6">
              <h2 className="font-display font-black text-2xl md:text-3xl tracking-tight">
                Jump back in
              </h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <Link
                to="/"
                data-testid="notfound-link-home"
                className="border border-[#0A0A0A] p-5 hover:bg-[#0A0A0A] hover:text-white transition-colors"
              >
                <HomeIcon className="w-5 h-5 mb-3" />
                <div className="font-display font-bold text-lg">Main directory</div>
                <div className="mt-1 text-xs opacity-70">
                  Search all 9,381 institutions
                </div>
              </Link>
              <Link
                to="/category/nbfc-icc"
                data-testid="notfound-link-nbfc"
                className="border border-[#0A0A0A] p-5 hover:bg-[#0A0A0A] hover:text-white transition-colors"
              >
                <Building2 className="w-5 h-5 mb-3" />
                <div className="font-display font-bold text-lg">NBFCs</div>
                <div className="mt-1 text-xs opacity-70">
                  Investment & Credit, HFC, MFI, Factor…
                </div>
              </Link>
              <Link
                to="/state/maharashtra"
                data-testid="notfound-link-state"
                className="border border-[#0A0A0A] p-5 hover:bg-[#0A0A0A] hover:text-white transition-colors"
              >
                <Compass className="w-5 h-5 mb-3" />
                <div className="font-display font-bold text-lg">By state</div>
                <div className="mt-1 text-xs opacity-70">
                  Start with Maharashtra (1,299) or browse all 35
                </div>
              </Link>
            </div>
          </div>
        </section>
      </main>

      <SiteFooter />
    </div>
  );
}
